import { z } from 'zod';

/**
 * Pedido de "exigir o build N" que espera a loja confirmar a disponibilidade
 * antes de virar `minBuild` em `/appConfig/{platform}`.
 *
 * Origem: #974 — 2026-10-15 15:42 +0200.
 *
 * Por quê: o `minBuild` sobe sempre para o build novo de um release de loja
 * (decisão do owner, 2026-10-15), mas só DEPOIS de a versão estar na loja —
 * subir antes trava a base com um "atualize" impossível de cumprir (#621). A
 * revisão da Apple/Google leva horas ou dias; o pedido precisa sobreviver a
 * isso fora de qualquer sessão de terminal, então mora no Firestore.
 *
 * Contrato: documento `releaseRollouts/{platform}_{build}` (id de
 * `releaseRolloutId`). Escrita só pelo servidor/deployer; leitura super-admin.
 *
 * Usado por: `scripts/request-min-build.sh` (cria, chamado por
 * `play-deploy.yml` e `appstore-deploy.yml`), `scripts/deploy-status.sh`
 * (lê, confirma, cancela), Functions `release/applyPendingMinBuild.ts`,
 * `release/playReleaseWatcher.ts` e `ios/ascWebhook.ts`.
 *
 * Arquitetura: o `minBuild` continua com um único guard
 * (`minBuild ≤ latestBuild`, `apps/functions/src/release/minBuildGuard.ts`),
 * compartilhado com a callable manual `setAppVersionConfig` (#655).
 *
 * Dados — `releaseRollouts/android_265`:
 *   {
 *     "platform": "android", "build": 265, "version": "2026.10.15+265",
 *     "sha": "5f2b0f434…", "requestedBy": "https://github.com/…/runs/1",
 *     "requestedAt": 1760536920000,          // epoch ms
 *     "state": "AWAITING_STORE",
 *     "playProductionAt": 1760537000000,     // só android: watcher viu production 100%
 *     "confirmedBy": "owner@…", "confirmedAt": 1760540000000,  // só android, opcional
 *     "appliedAt": null, "evidence": null, "updatedAt": 1760537000000
 *   }
 *
 * Issues: #974 (processo), #655 (kill switch), #621 (minBuild > latestBuild).
 */
export const RELEASE_ROLLOUTS_COLLECTION = 'releaseRollouts';

/** Plataformas de loja. Web/admin não passam por aqui: o release força sozinho. */
export const ReleaseRolloutPlatformSchema = z.enum(['android', 'ios']);
export type ReleaseRolloutPlatform = z.infer<typeof ReleaseRolloutPlatformSchema>;
export const ReleaseRolloutPlatform = ReleaseRolloutPlatformSchema.enum;

/**
 * Ciclo de vida do pedido.
 * - `AWAITING_STORE`: registrado, a loja ainda não confirmou.
 * - `MIN_BUILD_APPLIED`: loja confirmou e o `minBuild` subiu (terminal).
 * - `SUPERSEDED`: um build maior foi aplicado antes deste (terminal).
 * - `CANCELLED`: o owner segurou o release (`deploy-status.sh --cancel`).
 */
export const ReleaseRolloutStateSchema = z.enum([
  'AWAITING_STORE',
  'MIN_BUILD_APPLIED',
  'SUPERSEDED',
  'CANCELLED',
]);
export type ReleaseRolloutState = z.infer<typeof ReleaseRolloutStateSchema>;
export const ReleaseRolloutState = ReleaseRolloutStateSchema.enum;

export const ReleaseRolloutSchema = z.object({
  platform: ReleaseRolloutPlatformSchema,
  build: z.number().int().positive(),
  /** `YYYY.MM.DD+BUILD` — vira `latestVersion` quando a loja confirmar (android). */
  version: z.string().min(1),
  sha: z.string().min(7),
  requestedBy: z.string().min(1),
  requestedAt: z.number().int().nonnegative(),
  state: ReleaseRolloutStateSchema,
  /** Android: quando o watcher viu o versionCode em production `completed` 100%. */
  playProductionAt: z.number().int().nonnegative().nullable().optional(),
  /** Android: confirmação manual do owner de que a versão está na loja. */
  confirmedBy: z.string().nullable().optional(),
  confirmedAt: z.number().int().nonnegative().nullable().optional(),
  appliedAt: z.number().int().nonnegative().nullable().optional(),
  /** Texto curto do que provou a disponibilidade (vai também para o audit). */
  evidence: z.string().nullable().optional(),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
});
export type ReleaseRollout = z.infer<typeof ReleaseRolloutSchema>;

/** Id determinístico: o mesmo build pedido duas vezes cai no mesmo documento. */
export function releaseRolloutId(platform: ReleaseRolloutPlatform, build: number): string {
  return `${platform}_${String(build)}`;
}
